// src/components/TaskStats.tsx
import React from 'react';
import { ListChecks, FileEdit, Cog, Eye, Ban, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Task } from '../api/tasks';

interface TaskStatsProps {
  tasks: Task[];
  onStatusClick: (status: string) => void;
}

type Tone = 'all' | 'todo' | 'progress' | 'review' | 'blocked' | 'done' | 'overdue';

const TONE_STYLES: Record<Tone, { bg: string; border: string; iconBg: string; iconFg: string; label: string; value: string }> = {
  all:      { bg: '#d1fae5', border: '#86efac', iconBg: '#bbf7d0', iconFg: '#047857', label: '#047857', value: '#064e3b' },
  todo:     { bg: '#e2e8f0', border: '#cbd5e1', iconBg: '#cbd5e1', iconFg: '#334155', label: '#334155', value: '#0f172a' },
  progress: { bg: '#dbeafe', border: '#93c5fd', iconBg: '#bfdbfe', iconFg: '#1d4ed8', label: '#1e40af', value: '#1e3a8a' },
  review:   { bg: '#fef3c7', border: '#fcd34d', iconBg: '#fde68a', iconFg: '#b45309', label: '#92400e', value: '#78350f' },
  blocked:  { bg: '#fee2e2', border: '#fca5a5', iconBg: '#fecaca', iconFg: '#b91c1c', label: '#b91c1c', value: '#7f1d1d' },
  done:     { bg: '#bbf7d0', border: '#4ade80', iconBg: '#86efac', iconFg: '#065f46', label: '#065f46', value: '#064e3b' },
  overdue:  { bg: '#fecaca', border: '#f87171', iconBg: '#fca5a5', iconFg: '#991b1b', label: '#991b1b', value: '#7f1d1d' },
};

export function TaskStats({ tasks, onStatusClick }: TaskStatsProps) {
  const overdueCount = tasks.filter(t => t.is_overdue && t.status !== 'DONE' && t.status !== 'CANCELLED').length;

  const stats: Array<{ label: string; value: number; icon: React.ReactNode; filter: string; tone: Tone }> = [
    { label: 'All',         value: tasks.length, icon: <ListChecks className="w-3.5 h-3.5" />,    filter: 'ALL',         tone: 'all' },
    { label: 'To Do',       value: tasks.filter(t => t.status === 'TODO').length,        icon: <FileEdit className="w-3.5 h-3.5" />,    filter: 'TODO',        tone: 'todo' },
    { label: 'In Progress', value: tasks.filter(t => t.status === 'IN_PROGRESS').length, icon: <Cog className="w-3.5 h-3.5" />,         filter: 'IN_PROGRESS', tone: 'progress' },
    { label: 'In Review',   value: tasks.filter(t => t.status === 'IN_REVIEW').length,   icon: <Eye className="w-3.5 h-3.5" />,         filter: 'IN_REVIEW',   tone: 'review' },
    { label: 'Blocked',     value: tasks.filter(t => t.status === 'BLOCKED').length,     icon: <Ban className="w-3.5 h-3.5" />,         filter: 'BLOCKED',     tone: 'blocked' },
    { label: 'Completed',   value: tasks.filter(t => t.status === 'DONE').length,        icon: <CheckCircle2 className="w-3.5 h-3.5" />, filter: 'DONE',        tone: 'done' },
  ];

  const renderTile = (label: string, value: number, icon: React.ReactNode, filter: string, tone: Tone) => {
    const t = TONE_STYLES[tone];
    return (
      <button
        key={label}
        onClick={() => onStatusClick(filter)}
        className="text-left transition-all"
        style={{
          background: t.bg,
          border: `1px solid ${t.border}`,
          borderRadius: 12,
          padding: '8px 10px',
          cursor: 'pointer',
          boxShadow: 'var(--gt-elev-1)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          minWidth: 0,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = 'var(--gt-elev-2)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'var(--gt-elev-1)'; }}
      >
        <span style={{
          width: 22, height: 22, borderRadius: 7,
          background: t.iconBg, color: t.iconFg,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {icon}
        </span>
        <div className="flex flex-col min-w-0" style={{ lineHeight: 1.1 }}>
          <span className="gt-eyebrow" style={{ marginBottom: 0, color: t.label, fontSize: 9, letterSpacing: '0.06em' }}>
            {label}
          </span>
          <span className="gt-display gt-numerals" style={{
            fontSize: 16, fontWeight: 800, letterSpacing: '-0.02em',
            color: t.value, marginTop: 1,
          }}>
            {value}
          </span>
        </div>
      </button>
    );
  };

  const cols = overdueCount > 0 ? 7 : 6;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
      {stats.map((s) => renderTile(s.label, s.value, s.icon, s.filter, s.tone))}
      {overdueCount > 0 && renderTile('Overdue', overdueCount, <AlertTriangle className="w-3.5 h-3.5" />, 'OVERDUE', 'overdue')}
    </div>
  );
}
